import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, GraduationCap, MapPin, Calendar, Clock, Wallet, BookOpen,
    Users, Filter, Search, Bookmark, BookmarkCheck, Send, Inbox,
    UserCheck, Languages, Target, Layers, AlertTriangle, X,
} from "lucide-react";
import { tutorApi, formatApiError } from "../lib/tutorApi";
import TutorMobileLayout, { MobileSkeletonCard } from "../components/tutor/TutorMobileLayout";

const SORT_OPTIONS = [
    { value: "match", label: "Best Match" },
    { value: "latest", label: "Latest" },
    { value: "fee_desc", label: "Fee: High → Low" },
    { value: "fee_asc", label: "Fee: Low → High" },
];

export default function TutorLeads() {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [filters, setFilters] = useState({
        subject: "", grade: "", board: "", mode: "", area: "",
        min_fee: "", max_fee: "", sort: "match",
        only_bookmarked: false, only_applied: false,
    });
    const [showFilters, setShowFilters] = useState(false);
    const [busy, setBusy] = useState(null);

    const fetchFeed = useCallback(async (f) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            Object.entries(f || filters).forEach(([k, v]) => {
                if (v === "" || v == null || v === false) return;
                params.set(k, String(v));
            });
            params.set("limit", "40");
            const { data } = await tutorApi.get(`/tutor/me/leads?${params.toString()}`);
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setLoading(false); }
    }, [filters]);

    useEffect(() => { fetchFeed(); }, [fetchFeed]);

    const updateFilter = (k, v) => {
        const nf = { ...filters, [k]: v };
        setFilters(nf);
        if (typeof v === "boolean" || k === "sort" || k === "grade" || k === "board" || k === "mode") {
            fetchFeed(nf);
        }
    };

    const applyToLead = async (lead) => {
        setBusy(lead.id);
        try {
            const { data } = await tutorApi.post(`/tutor/me/leads/${lead.id}/apply`);
            if (data.already_applied) toast.info("You've already applied to this lead.");
            else toast.success(`Applied! Admin notified (${data.match_score}% match).`);
            setItems((prev) => prev.map((x) => x.id === lead.id ? { ...x, applied: true } : x));
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setBusy(null); }
    };

    const withdraw = async (lead) => {
        if (!window.confirm("Withdraw your application?")) return;
        setBusy(lead.id);
        try {
            await tutorApi.delete(`/tutor/me/leads/${lead.id}/apply`);
            toast.success("Application withdrawn");
            setItems((prev) => prev.map((x) => x.id === lead.id ? { ...x, applied: false } : x));
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setBusy(null); }
    };

    const toggleBookmark = async (lead) => {
        setBusy(lead.id + "-b");
        try {
            const { data } = await tutorApi.post(`/tutor/me/leads/${lead.id}/bookmark`);
            setItems((prev) => prev.map((x) => x.id === lead.id ? { ...x, bookmarked: data.bookmarked } : x));
            toast.success(data.bookmarked ? "Saved for later" : "Removed from saved");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setBusy(null); }
    };

    const [searchQ, setSearchQ] = useState("");
    const [bestForMe, setBestForMe] = useState(false);

    // V34.7 — Mode quick-tabs (All / Online / Home Tuition) match BookMyTutor.
    const setMode = (m) => {
        const nf = { ...filters, mode: m };
        setFilters(nf);
        fetchFeed(nf);
    };

    // V34.7 — "Best for me" toggle = filters.sort='match' + only_bookmarked off.
    const toggleBestForMe = () => {
        const next = !bestForMe;
        setBestForMe(next);
        const nf = { ...filters, sort: next ? "match" : "latest" };
        setFilters(nf);
        fetchFeed(nf);
    };

    // Client-side search filter (avoids a backend roundtrip per keystroke).
    const visibleItems = searchQ.trim()
        ? items.filter((x) => {
            const blob = `${x.grade || ""} ${x.subjects || ""} ${x.board || ""} ${x.city || ""} ${x.area || ""}`.toLowerCase();
            return blob.includes(searchQ.trim().toLowerCase());
        })
        : items;

    return (
        <TutorMobileLayout title="Available Tuitions"
            action={
                <button onClick={() => setShowFilters(true)} data-testid="tutor-leads-toggle-filters"
                    className="bg-white/15 hover:bg-white/25 backdrop-blur rounded-full p-2 active:scale-95 transition">
                    <Filter className="w-4 h-4" />
                </button>
            }
        >
            {/* V34.7 — BookMyTutor search + Best-for-me row */}
            <div className="flex items-center gap-2 mb-3" data-testid="tutor-leads-searchbar">
                <div className="flex-1 flex items-center gap-2 bg-rose-50 ring-1 ring-rose-200 rounded-full px-3 py-2 min-h-[44px]">
                    <Search className="w-4 h-4 text-rose-500" />
                    <input
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                        placeholder='Search "Home Tuition"'
                        className="flex-1 bg-transparent text-sm outline-none placeholder:text-rose-400 text-[#0F172A]"
                        data-testid="tutor-leads-search-input"
                    />
                </div>
                <button onClick={toggleBestForMe} data-testid="tutor-leads-best-for-me"
                    className={`px-4 min-h-[44px] rounded-full text-sm font-bold transition ${
                        bestForMe ? "bg-rose-600 text-white shadow" : "bg-white text-rose-600 ring-1 ring-rose-200"
                    }`}>
                    Best for me
                </button>
            </div>

            {/* V34.7 — Mode quick-tabs (All / Online / Home Tuition) */}
            <div className="grid grid-cols-3 gap-2 mb-3" data-testid="tutor-leads-mode-tabs">
                {[
                    { id: "", label: "All", icon: Layers },
                    { id: "online", label: "Online", icon: Languages },
                    { id: "home", label: "Home Tuition", icon: BookOpen },
                ].map((m) => {
                    const Icon = m.icon;
                    const active = (filters.mode || "").toLowerCase().includes(m.id.toLowerCase()) || (m.id === "" && !filters.mode);
                    return (
                        <button key={m.id || "all"} onClick={() => setMode(m.id ? (m.id === "online" ? "Live Online" : "Home Tuition") : "")}
                            data-testid={`tutor-leads-mode-${m.id || "all"}`}
                            className={`min-h-[42px] rounded-full text-xs font-bold transition inline-flex items-center justify-center gap-1.5 ${
                                active ? "bg-white text-rose-600 shadow ring-1 ring-rose-200" : "bg-rose-500/20 text-white ring-1 ring-white/30"
                            }`}>
                            <Icon className="w-3.5 h-3.5" /> {m.label}
                        </button>
                    );
                })}
            </div>

            {/* Total count header */}
            <div className="bg-slate-50 -mx-4 px-4 py-3 mb-3 border-b border-slate-200" data-testid="tutor-leads-total-count">
                <h3 className="font-heading text-base font-bold text-[#0F172A]">
                    Total Available Tuition: <span data-testid="tutor-leads-count-number">{visibleItems.length}</span>
                </h3>
            </div>

            {/* Quick sort tabs (kept for power users; hidden on smallest screens) */}
            <div className="hidden sm:flex gap-1.5 overflow-x-auto -mx-1 px-1 mb-3 pb-1" data-testid="tutor-leads-sort-tabs">
                {SORT_OPTIONS.map((s) => (
                    <button key={s.value} onClick={() => updateFilter("sort", s.value)} data-testid={`tutor-leads-sort-${s.value}`}
                        className={`px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap flex-shrink-0 transition ${filters.sort === s.value ? "bg-rose-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
                        {s.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="space-y-3">
                    {[...Array(3)].map((_, i) => <MobileSkeletonCard key={i} />)}
                </div>
            ) : visibleItems.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center" data-testid="tutor-leads-empty">
                    <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    <p className="font-semibold text-slate-700">{searchQ ? "No tuitions match that search." : "No matching leads right now."}</p>
                    <p className="text-xs mt-1 text-slate-500">
                        {searchQ ? "Try a different keyword or clear the search." : "Update your profile (subjects, grades, location) to broaden matches."}
                    </p>
                    {!searchQ && <Link to="/tutor/profile" className="inline-block mt-3 text-xs font-bold text-rose-600">Edit profile →</Link>}
                </div>
            ) : (
                <div className="space-y-3" data-testid="tutor-leads-list">
                    {visibleItems.map((lead) => (
                        <LeadCard key={lead.id} lead={lead}
                            onApply={() => applyToLead(lead)} onWithdraw={() => withdraw(lead)}
                            onBookmark={() => toggleBookmark(lead)} busy={busy} />
                    ))}
                </div>
            )}

            {showFilters && (
                <FilterSheet filters={filters} setFilters={setFilters} updateFilter={updateFilter}
                    onApply={() => { fetchFeed(); setShowFilters(false); }}
                    onClose={() => setShowFilters(false)} />
            )}
        </TutorMobileLayout>
    );
}

function FilterSheet({ filters, setFilters, updateFilter, onApply, onClose }) {
    const inputCls = "w-full border border-slate-300 rounded-xl px-3 py-2 text-sm";
    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end" data-testid="tutor-leads-filter-sheet">
            <div className="bg-white rounded-t-3xl w-full max-h-[85vh] overflow-y-auto pb-6">
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between z-10">
                    <h3 className="font-bold text-[#0F172A]">Filters</h3>
                    <button onClick={onClose} data-testid="tutor-leads-filter-close"><X className="w-5 h-5 text-slate-400" /></button>
                </div>
                <div className="px-4 py-4 space-y-3">
                    <Field label="Subject"><input value={filters.subject} onChange={(e) => setFilters({ ...filters, subject: e.target.value })} data-testid="tutor-leads-filter-subject" className={inputCls} placeholder="e.g. Maths" /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Grade"><input value={filters.grade} onChange={(e) => updateFilter("grade", e.target.value)} data-testid="tutor-leads-filter-grade" className={inputCls} placeholder="VIII" /></Field>
                        <Field label="Board"><input value={filters.board} onChange={(e) => updateFilter("board", e.target.value)} data-testid="tutor-leads-filter-board" className={inputCls} placeholder="CBSE" /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Mode"><input value={filters.mode} onChange={(e) => updateFilter("mode", e.target.value)} data-testid="tutor-leads-filter-mode" className={inputCls} placeholder="Home Tuition" /></Field>
                        <Field label="Area / PIN"><input value={filters.area} onChange={(e) => setFilters({ ...filters, area: e.target.value })} data-testid="tutor-leads-filter-area" className={inputCls} placeholder="560100" /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Min fee"><input type="number" value={filters.min_fee} onChange={(e) => setFilters({ ...filters, min_fee: e.target.value })} data-testid="tutor-leads-filter-min-fee" className={inputCls} placeholder="₹" /></Field>
                        <Field label="Max fee"><input type="number" value={filters.max_fee} onChange={(e) => setFilters({ ...filters, max_fee: e.target.value })} data-testid="tutor-leads-filter-max-fee" className={inputCls} placeholder="₹" /></Field>
                    </div>
                    <div className="flex gap-3 pt-2">
                        <label className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm">
                            <input type="checkbox" checked={filters.only_applied} onChange={(e) => updateFilter("only_applied", e.target.checked)} data-testid="tutor-leads-filter-applied" className="accent-orange-600" />
                            Applied
                        </label>
                        <label className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm">
                            <input type="checkbox" checked={filters.only_bookmarked} onChange={(e) => updateFilter("only_bookmarked", e.target.checked)} data-testid="tutor-leads-filter-bookmarked" className="accent-orange-600" />
                            Saved
                        </label>
                    </div>
                    <button onClick={onApply} data-testid="tutor-leads-filter-search"
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-3 rounded-xl inline-flex items-center justify-center gap-2 active:scale-[0.98] transition">
                        <Search className="w-4 h-4" /> Apply filters
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider mb-1 block">{label}</span>
            {children}
        </label>
    );
}

function LeadCard({ lead, onApply, onWithdraw, onBookmark, busy }) {
    const scoreTone = lead.match_score >= 80 ? "bg-emerald-500 text-white"
        : lead.match_score >= 50 ? "bg-amber-500 text-white"
        : "bg-rose-500 text-white";
    const isApplyBusy = busy === lead.id;
    const isBookmarkBusy = busy === lead.id + "-b";

    return (
        <article className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition active:scale-[0.99]" data-testid={`tutor-lead-card-${lead.id}`}>
            {/* Match score badge */}
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">{lead.grade || "—"}</span>
                    {lead.board && <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md">{lead.board}</span>}
                    {lead.mode && <span className="text-[10px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700 px-2 py-0.5 rounded-md">{lead.mode}</span>}
                    {lead.students_count > 1 && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700 px-2 py-0.5 rounded-md inline-flex items-center gap-0.5">
                            <Users className="w-3 h-3" /> {lead.students_count}
                        </span>
                    )}
                </div>
                <span className={`text-[11px] font-bold px-2 py-1 rounded-lg ${scoreTone}`} data-testid={`tutor-lead-score-${lead.id}`}>
                    {Math.round(lead.match_score)}%
                </span>
            </div>

            <div className="font-bold text-[#0F172A] text-base inline-flex items-center gap-1.5">
                <BookOpen className="w-4 h-4 text-slate-400" />
                {lead.subjects || (lead.wants_all_major_subjects ? "All Major Subjects" : "—")}
            </div>

            {/* Compact details */}
            <div className="grid grid-cols-1 gap-1 text-[11px] text-slate-600 mt-2">
                {(lead.area || lead.city) && (
                    <div className="flex items-center gap-1.5">
                        <MapPin className="w-3 h-3 text-slate-400" />
                        <span className="truncate">{[lead.area, lead.city, lead.state].filter(Boolean).join(", ")} {lead.pin_code && `· ${lead.pin_code}`}</span>
                    </div>
                )}
                {lead.preferred_demo_at && (
                    <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3 text-slate-400" /> {new Date(lead.preferred_demo_at).toLocaleString()}</div>
                )}
                <div className="flex items-center gap-1.5 font-bold text-emerald-700">
                    <Wallet className="w-3 h-3" /> {lead.fee_display}
                </div>
                {(lead.preferred_days?.length || lead.preferred_time_window) && (
                    <div className="flex items-center gap-1.5">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span>{lead.preferred_days?.join(", ")} {lead.preferred_time_window ? `· ${lead.preferred_time_window}` : ""}</span>
                    </div>
                )}
                {lead.language_preference?.length > 0 && (
                    <div className="flex items-center gap-1.5"><Languages className="w-3 h-3 text-slate-400" /> {lead.language_preference.join(", ")}</div>
                )}
                {lead.exam_goal && (
                    <div className="flex items-center gap-1.5"><Target className="w-3 h-3 text-slate-400" /> {lead.exam_goal}</div>
                )}
                {lead.curriculum_preference && (
                    <div className="flex items-center gap-1.5"><Layers className="w-3 h-3 text-slate-400" /> {lead.curriculum_preference}</div>
                )}
            </div>

            {(lead.special_notes || lead.special_requirements) && (
                <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mt-2 inline-flex items-start gap-1.5">
                    <AlertTriangle className="w-3 h-3 mt-0.5 text-amber-600 flex-shrink-0" />
                    <span>{lead.special_notes || lead.special_requirements}</span>
                </div>
            )}

            {/* Actions row */}
            <div className="flex gap-2 mt-3" data-testid={`tutor-lead-actions-${lead.id}`}>
                {lead.applied ? (
                    <button onClick={onWithdraw} disabled={isApplyBusy} data-testid={`tutor-lead-withdraw-${lead.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2.5 rounded-xl border-2 border-emerald-300 bg-emerald-50 text-emerald-700 text-xs font-bold active:scale-95 transition disabled:opacity-60">
                        {isApplyBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />} Applied
                    </button>
                ) : (
                    <button onClick={onApply} disabled={isApplyBusy} data-testid={`tutor-lead-apply-${lead.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-bold active:scale-95 transition disabled:opacity-60">
                        {isApplyBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Apply Now
                    </button>
                )}
                <button onClick={onBookmark} disabled={isBookmarkBusy} data-testid={`tutor-lead-bookmark-${lead.id}`} data-bookmarked={lead.bookmarked ? "true" : "false"}
                    className={`px-3 py-2.5 rounded-xl border-2 text-xs font-bold active:scale-95 transition disabled:opacity-60 ${lead.bookmarked ? "bg-blue-50 border-blue-300 text-blue-700" : "border-slate-300 text-slate-600"}`}>
                    {isBookmarkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : lead.bookmarked ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
                </button>
            </div>

            <p className="text-[9px] text-slate-400 mt-2 text-center">Contact info revealed after admin assigns you</p>
        </article>
    );
}

